/**
 * Human-readable labels for HealthKit type identifiers, metadata keys, and enum values.
 * Used by the Firehose view to make raw HK identifiers legible.
 *
 * Sources of truth: Apple HealthKit headers + developer.apple.com docs.
 * If a type/key isn't in the table, the view falls back to the raw identifier
 * (stripped of HKQuantityTypeIdentifier / HKCategoryTypeIdentifier / HKMetadataKey prefix).
 */

export interface TypeLabel {
  /** Human name shown as the bucket title. */
  name: string;
  /** One-line plain-English description of what this is. */
  blurb: string;
  /** Friendly unit string shown next to values (overrides the canonical HKUnit). */
  unitLabel?: string;
}

export const TYPE_LABELS: Record<string, TypeLabel> = {
  // Cardio
  HKQuantityTypeIdentifierHeartRate: {
    name: "Heart rate",
    blurb: "Beats per minute, sampled from the Watch's optical sensor",
    unitLabel: "bpm",
  },
  HKQuantityTypeIdentifierRestingHeartRate: {
    name: "Resting heart rate",
    blurb: "Lowest HR observed when you're still, a daily metric, rare during workouts",
    unitLabel: "bpm",
  },
  HKQuantityTypeIdentifierHeartRateVariabilitySDNN: {
    name: "HRV (SDNN)",
    blurb: "Standard deviation of beat-to-beat intervals, a recovery & stress proxy",
    unitLabel: "ms",
  },
  HKQuantityTypeIdentifierHeartRateRecoveryOneMinute: {
    name: "1-min HR recovery",
    blurb: "How many bpm your heart rate drops in the minute after exertion",
    unitLabel: "bpm",
  },
  HKQuantityTypeIdentifierRespiratoryRate: {
    name: "Respiratory rate",
    blurb: "Breaths per minute, inferred from HR/motion",
    unitLabel: "breaths/min",
  },
  HKQuantityTypeIdentifierOxygenSaturation: {
    name: "Blood oxygen (SpO2)",
    blurb: "% hemoglobin carrying oxygen, Series 6+ measures on-wrist",
    unitLabel: "%",
  },
  HKQuantityTypeIdentifierVO2Max: {
    name: "VO2 max",
    blurb: "Estimated peak oxygen uptake, Apple's cardio fitness metric",
    unitLabel: "mL/kg·min",
  },

  // Energy
  HKQuantityTypeIdentifierActiveEnergyBurned: {
    name: "Active calories",
    blurb: "Energy burned by movement, ABOVE your resting baseline",
    unitLabel: "kcal",
  },
  HKQuantityTypeIdentifierBasalEnergyBurned: {
    name: "Resting calories",
    blurb: "Energy your body burns just being alive, basal metabolism",
    unitLabel: "kcal",
  },
  HKQuantityTypeIdentifierPhysicalEffort: {
    name: "Physical effort",
    blurb: "Apple's per-minute effort estimate (kcal/kg·hr ≈ METs)",
    unitLabel: "kcal/kg·hr",
  },

  // Swimming-specific
  HKQuantityTypeIdentifierDistanceSwimming: {
    name: "Distance, swimming",
    blurb: "Pool length × completed laps (pool swims), or GPS distance (open water)",
    unitLabel: "m",
  },
  HKQuantityTypeIdentifierSwimmingStrokeCount: {
    name: "Stroke count",
    blurb: "Strokes counted from wrist motion in each interval",
    unitLabel: "strokes",
  },

  // Walking / running
  HKQuantityTypeIdentifierStepCount: {
    name: "Step count",
    blurb: "Steps counted by accelerometer (usually 0 during swims)",
    unitLabel: "steps",
  },
  HKQuantityTypeIdentifierDistanceWalkingRunning: {
    name: "Distance, walking/running",
    blurb: "GPS or pedometer-derived distance for non-swim activities",
    unitLabel: "m",
  },
  HKQuantityTypeIdentifierRunningSpeed: {
    name: "Running speed",
    blurb: "Real-time pace, running only",
    unitLabel: "m/s",
  },
  HKQuantityTypeIdentifierRunningPower: {
    name: "Running power",
    blurb: "Mechanical work output while running, in watts",
    unitLabel: "W",
  },

  // Environment / other
  HKQuantityTypeIdentifierBodyTemperature: {
    name: "Body temperature",
    blurb: "Core or skin temperature, manual entry or compatible thermometer",
    unitLabel: "°C",
  },
  HKQuantityTypeIdentifierEnvironmentalAudioExposure: {
    name: "Audio exposure",
    blurb: "Loudness around you, dBA from the Watch microphone",
    unitLabel: "dB",
  },
};

/** Strip the canonical prefix and CamelCase-split for fallback display. */
export function prettifyIdentifier(raw: string): string {
  return raw
    .replace(/^HKQuantityTypeIdentifier/, "")
    .replace(/^HKCategoryTypeIdentifier/, "")
    .replace(/^HKMetadataKey/, "")
    .replace(/([a-z])([A-Z])/g, "$1 $2");
}

/** Look up a friendly label, falling back to a prettified identifier. */
export function typeLabel(raw: string): TypeLabel {
  return (
    TYPE_LABELS[raw] ?? {
      name: prettifyIdentifier(raw),
      blurb: `Raw HealthKit type, no friendly label defined yet`,
    }
  );
}

// ============================================================================
// Metadata key decoders
// ============================================================================

export interface MetadataDecoder {
  label: string;
  /** Optional value transformer — turns raw value (number/string) into friendly text. */
  decode?: (v: unknown) => string;
}

/**
 * HealthKit metadata key pairs. The runtime value of an `HKMetadataKey*`
 * ObjC constant is the SAME string WITHOUT the "HKMetadataKey" prefix —
 * e.g. `HKMetadataKeySwimmingStrokeStyle` → `"HKSwimmingStrokeStyle"`.
 * Real HK data uses the bare form; some SDK versions emit the prefixed form.
 * Look up via both. Centralized here so a new key drift can't silently break
 * the deriver and the firehose decoder independently.
 */
export const HK_META_KEYS = {
  strokeStyle: ["HKSwimmingStrokeStyle", "HKMetadataKeySwimmingStrokeStyle"],
  lapLength: ["HKLapLength", "HKMetadataKeyLapLength"],
  swimLocation: ["HKSwimmingLocationType", "HKMetadataKeySwimmingLocationType"],
  waterSalinity: ["HKWaterSalinity", "HKMetadataKeyWaterSalinity"],
} as const;

/** Look up the first matching key from a list. Returns undefined if no key is present. */
export function readMetaValue(md: Record<string, unknown> | null | undefined, keys: readonly string[]): unknown {
  if (!md) return undefined;
  for (const k of keys) if (k in md) return md[k];
  return undefined;
}

/** Swimming stroke style enum (HKSwimmingStrokeStyle). */
export const SWIMMING_STROKE_STYLES: Record<number, string> = {
  0: "Unknown",
  1: "Mixed",
  2: "Freestyle",
  3: "Backstroke",
  4: "Breaststroke",
  5: "Butterfly",
  6: "Kickboard",
};

export function decodeStrokeStyle(v: unknown): string {
  const n = Number(v);
  if (!Number.isFinite(n)) return String(v);
  return SWIMMING_STROKE_STYLES[n] ?? `Unknown(${n})`;
}

function decodeSwimLocationType(v: unknown): string {
  const map: Record<number, string> = { 0: "Unknown", 1: "Pool", 2: "Open water" };
  const n = Number(v);
  return Number.isFinite(n) ? map[n] ?? `Unknown(${n})` : String(v);
}

function decodeWaterSalinity(v: unknown): string {
  const map: Record<number, string> = { 1: "Fresh water", 2: "Salt water" };
  const n = Number(v);
  return Number.isFinite(n) ? map[n] ?? `Unknown(${n})` : String(v);
}

export const METADATA_DECODERS: Record<string, MetadataDecoder> = {
  // Real HealthKit data uses the bare key form (no "HKMetadataKey" prefix) — that's
  // the runtime string value of the ObjC constants. We register both forms.
  HKSwimmingStrokeStyle: { label: "Stroke style", decode: decodeStrokeStyle },
  HKMetadataKeySwimmingStrokeStyle: { label: "Stroke style", decode: decodeStrokeStyle },
  HKSwimmingLocationType: { label: "Swim location", decode: decodeSwimLocationType },
  HKMetadataKeySwimmingLocationType: { label: "Swim location", decode: decodeSwimLocationType },
  HKLapLength: { label: "Lap length (m)" },
  HKMetadataKeyLapLength: { label: "Lap length (m)" },
  HKWaterSalinity: { label: "Water type", decode: decodeWaterSalinity },
  HKMetadataKeyWaterSalinity: { label: "Water type", decode: decodeWaterSalinity },
  HKMetadataKeyIndoorWorkout: {
    label: "Indoor workout",
    decode: (v) => (v === true || v === 1 || v === "1" ? "Yes" : "No"),
  },
  HKMetadataKeyAverageMETs: { label: "Average METs" },
  HKMetadataKeyWorkoutBrandName: { label: "Workout brand" },
  HKMetadataKeyHeartRateMotionContext: {
    label: "HR motion context",
    decode: (v) => {
      const map: Record<number, string> = { 0: "Not set", 1: "Sedentary", 2: "Active" };
      const n = Number(v);
      return Number.isFinite(n) ? map[n] ?? `Unknown(${n})` : String(v);
    },
  },
  // Common Apple-internal keys that show up but aren't documented prominently:
  HKExternalUUID: { label: "External UUID" },
  HKTimeZone: { label: "Time zone" },
  HKAlgorithmVersion: { label: "Algorithm version" },
  HKDateOfEarliestDataPoint: { label: "Earliest data point" },
};

export function decodeMetadataValue(key: string, value: unknown): string {
  const dec = METADATA_DECODERS[key];
  if (dec?.decode) return dec.decode(value);
  if (value === null || value === undefined) return "—";
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
}

export function metadataLabel(key: string): string {
  return METADATA_DECODERS[key]?.label ?? prettifyIdentifier(key);
}

// ============================================================================
// Workout event types (already decoded native-side, but kept here for blurb)
// ============================================================================

export const EVENT_TYPE_BLURBS: Record<string, string> = {
  lap: "End of a pool length, the watch detected a turn",
  segment: "Manually marked segment (e.g. main set vs warm-up)",
  pause: "Workout paused (auto or manual)",
  resume: "Workout resumed after a pause",
  motionPaused: "Auto-pause: wrist stopped moving",
  motionResumed: "Auto-resume: wrist moving again",
  marker: "Manual marker, Watch user pressed the lap button",
  pauseOrResumeRequest: "User requested pause/resume",
};
